<template>
  <div class="pb-8">
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <LayupSpinner :size="12" container-classes="" />
    </div>
    <div v-else-if="allOrders.length === 0" class="min-h-[500px] flex items-center justify-center">
      <NoActiveOrder />
    </div>
    <div v-else>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-[24px] font-semibold text-grey-light-900">Recent Purchases</h2>
        <button @click="toggleViewAll" class="text-brand-600 hover:text-brand-700 font-semibold text-[14px]">
          {{ showAll ? 'View Less' : 'View All' }}
        </button>
      </div>

      <div v-if="!showAll" class="relative">
        <Carousel
          :opts="{
            align: 'start',
            loop: false,
            dragFree: true,
          }"
          class="w-full"
          @init-api="onCarouselInit"
        >
          <CarouselContent class="md:ml-1">
            <CarouselItem v-for="order in allOrders" :key="order._id" class="pl-5 md:basis-1/2 xl:basis-1/3">
              <BoostRewardCard v-if="order.type === 'REWARDS'" :order="order" />
              <LaybyCard v-else :order="order" />
            </CarouselItem>
          </CarouselContent>
        </Carousel>
        <div class="relative h-2 bg-gray-200 rounded-full mt-4">
          <div
            class="absolute h-full w-1/3 bg-gray-300 rounded-full transition-all duration-300"
            :style="{ left: scrollProgress }"
          />
        </div>
      </div>

      <div v-else class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        <div v-for="order in allOrders" :key="order._id" class="md:basis-1/2 xl:basis-1/3">
          <BoostRewardCard v-if="order.type === 'REWARDS'" :order="order" />
          <LaybyCard v-else :order="order" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import BoostRewardCard from '@/components/shopper/boost-rewards/BoostRewardCard.vue';
import LaybyCard from '@/components/shopper/lay-by/LaybyCard.vue';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { NetworkError } from '@/errors';
import { fetchOrders } from '@/services/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';
import NoActiveOrder from './NoActiveOrder.vue';

const isLoading = ref(true);
const showAll = ref(false);
const allOrders = ref<ModelOrderResponse[]>([]);
const scrollProgress = ref('0%');

const onCarouselInit = (api: any) => {
  if (!api) return;

  const updateProgress = () => {
    const progress = Math.max(0, Math.min(1, api.scrollProgress()));
    const maxLeft = 100 - 33.333; // 100% - thumb width (1/3 = 33.333%)
    scrollProgress.value = `${progress * maxLeft}%`;
  };

  updateProgress();
  api.on('scroll', updateProgress);
  api.on('settle', updateProgress);
};

const toggleViewAll = () => {
  showAll.value = !showAll.value;
};

const getAllOrders = async () => {
  isLoading.value = true;
  try {
    const { orders } = await fetchOrders({
      populate: ['voucher', 'plans', 'plans.benefactor'],
    });
    allOrders.value = orders || [];
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('shopper-home', { status: 'network_error', channel: 'shopper' });
    } else {
      createException(error);
      addMetric('shopper-home', { status: 'error', channel: 'shopper' });
    }
  } finally {
    isLoading.value = false;
  }
};

const init = async () => {
  await getAllOrders();
};

init();
</script>
